import ConfiguratorComponent from './modules/configurator-component';
import PriceCalculation from './modules/price-calculation-matrix';

if (!customElements.get('matrix-configurator')) {
  class MatrixConfigurator extends ConfiguratorComponent {
    constructor() {
      super();

      // Selectors specific to the matrix configurator
      this.matrixColorPicker = document.querySelector('matrix-color-picker');

      // Steps
      const steps = this.defineSteps(this.productData['product_type']);
      const progressBarSteps = this.defineProgressBarSteps(this.productData['product_type']);
      if (!steps.length || !progressBarSteps.length) {
        return;
      }

      // Options
      this.options = {
        debug: false,
        steps: steps,
        progressBarSteps: progressBarSteps,
      };

      // state
      this.state = {
        step: 'size',
        progress: [],
      };

      // Get matrix metaobject data
      this.matrixData = this.getMatrixData();
      if (this.options.debug) console.log('matrix', this.matrixData);

      // Get surcharge data
      this.surchargeData = this.getSurchargeData();
      if (this.options.debug) console.log('surchargeData', this.surchargeData);

      // Free text input for location
      this.locationFreeInput = handleize(this.dataset.locationFreeInput || '');
    }

    /**
     * Define steps based on product type
     * @returns {Array} steps
     */
    defineSteps() {
      let steps = [...this.querySelectorAll('[data-step]')].map((step) =>
        step.getAttribute('data-step')
      );
      steps = steps.filter((step, index) => steps.indexOf(step) === index);
      return steps;
    }

    /**
     * Define progress bar steps based on product type
     * @returns {Array} steps
     */
    defineProgressBarSteps() {
      let steps = [...this.querySelectorAll('[data-progress]')].map((step) =>
        step.getAttribute('data-progress')
      );
      steps = steps.filter((step, index) => steps.indexOf(step) === index);
      return steps;
    }

    /**
     * Calculate curtain price
     * @returns {object} calculation
     */
    priceCalculation() {
      return new PriceCalculation(
        this.productData,
        this.matrixData,
        this.surchargeData,
        this.formData
      );
    }

    /**
     * @returns Matrix metaobject data
     */
    getMatrixData() {
      const matrixData = this.querySelector('[data-matrix-json]')
        ? parseJson(this.querySelector('[data-matrix-json]').textContent)
        : {};

      return matrixData;
    }

    /**
     * @returns Surcharge metaobject data
     */
    getSurchargeData() {
      const surchargeData = this.querySelector('[data-surcharges-json]')
        ? parseJson(this.querySelector('[data-surcharges-json]').textContent)
        : {};

      return surchargeData;
    }

    /**
     * Format add to cart item with properties
     * @param {object} calculation
     * @returns {object} line item with properties
     */
    formatLineItem(calculation) {
      if (!this.formData || !calculation) {
        return;
      }

      // Return formatted line item with properties
      return {
        id: parseInt(this.formData.id),
        quantity: parseInt(this.formData.quantity),
        properties: {
          _ref: new Date().getTime(),
          _token: calculation.token,
          _compare_at_price: calculation.compare_at_price,
          _width: this.formData.width,
          _height: this.formData.height,
          ...(this.formData.product_type && {
            _product_type: this.formData.product_type,
          }),
          ...(this.formData.color && {
            _color: this.formData.color,
          }),
          ...(this.formData.material && {
            _material: this.formData.material,
          }),
          ...(this.formData.slat_width && {
            _slat_width: this.formData.slat_width,
          }),
          ...(this.formData.window_type && {
            _window_type: this.formData.window_type,
          }),
          ...(this.formData.placement && {
            _placement: this.formData.placement,
          }),
          ...(this.formData.chain && {
            _chain: this.formData.chain,
          }),
          ...(this.formData.control_direction && {
            _control_direction: this.formData.control_direction,
          }),
          ...(this.formData.control_location && {
            _control_location: this.formData.control_location,
          }),
          ...(this.formData.control_type && {
            _control_type: this.formData.control_type,
          }),
          ...(this.formData.sheeting_type && {
            _sheeting_type: this.formData.sheeting_type,
          }),
          ...(this.formData.tape && {
            _tape: this.formData.tape,
          }),
          ...(this.formData.option_id && {
            _option_id: this.formData.option_id,
          }),
          ...(this.formData.option_sku && {
            _option_sku: this.formData.option_sku,
          }),
          ...(this.formData.option_title && {
            _option_title: this.formData.option_title,
          }),
          ...(this.formData.option_color_name && {
            _option_color: this.formData.option_color_name,
          }),
          ...(this.formData.type_of_attachment && {
            _type_of_attachment: this.formData.type_of_attachment,
          }),
          ...(this.formData.draw_rod && {
            _draw_rod: this.formData.draw_rod,
          }),
          ...(this.formData.delivery_time && {
            _delivery_time: this.formData.delivery_time,
          }),
          ...(this.formData.location && {
            _location: this.formData.location,
          }),
          ...(this.formData.location_free_input && {
            _location_free_input: this.formData.location_free_input,
          }),
          [this.translateKeyValue('width')]: this.translateKeyValue(this.formData.width + ' cm'),
          [this.translateKeyValue('height')]: this.translateKeyValue(this.formData.height + ' cm'),
          ...(this.formData.color && {
            [this.translateKeyValue('color')]: this.translateKeyValue(this.formData.color),
          }),
          ...(this.formData.material && {
            [this.translateKeyValue('material')]: this.translateKeyValue(this.formData.material),
          }),
          ...(this.formData.slat_width && {
            [this.translateKeyValue('slat_width')]: this.translateKeyValue(
              this.formData.slat_width + ' mm'
            ),
          }),
          ...(this.formData.window_type && {
            [this.translateKeyValue('window_type')]: this.translateKeyValue(
              this.formData.window_type
            ),
          }),
          ...(this.formData.placement && {
            [this.translateKeyValue('placement')]: this.translateKeyValue(this.formData.placement),
          }),
          ...(this.formData.control_direction && {
            [this.translateKeyValue('control_direction')]: this.translateKeyValue(
              this.formData.control_direction
            ),
          }),
          ...(this.formData.control_location && {
            [this.translateKeyValue('control_location')]: this.translateKeyValue(
              this.formData.control_location
            ),
          }),
          ...(this.formData.control_type && {
            [this.translateKeyValue('control_type')]: this.translateKeyValue(
              this.formData.control_type
            ),
          }),
          ...(this.formData.chain && {
            [this.translateKeyValue('chain')]: this.translateKeyValue(this.formData.chain),
          }),
          ...(this.formData.sheeting_type && {
            [this.translateKeyValue('sheeting_type')]: this.translateKeyValue(
              this.formData.sheeting_type
            ),
          }),
          ...(this.formData.tape && {
            [this.translateKeyValue('tape')]: this.translateKeyValue(this.formData.tape),
          }),
          ...(this.formData.option_color_name && {
            [this.translateKeyValue('option_color_name')]: this.translateKeyValue(
              this.formData.option_color_name
            ),
          }),
          ...(this.formData.type_of_attachment && {
            [this.translateKeyValue('type_of_attachment')]: this.translateKeyValue(
              this.formData.type_of_attachment
            ),
          }),
          ...(this.formData.draw_rod && {
            [this.translateKeyValue('draw_rod')]: this.translateKeyValue(this.formData.draw_rod),
          }),
          ...(this.formData.location &&
            !this.formData.location_free_input && {
              [this.translateKeyValue('location')]: this.translateKeyValue(this.formData.location),
            }),
          ...(this.formData.location_free_input && {
            [this.translateKeyValue('location')]: this.translateKeyValue(
              this.formData.location_free_input
            ),
          }),
        },
      };
    }

    /**
     * Handle errors from price calculation
     * @param {Array} errors
     */
    handleErrors(errors) {
      super.handleErrors(errors);

      errors.map((error) => {
        const message = this.translations[error.handle]
          ? this.translations[error.handle]
          : error.message;
        switch (error.type) {
          case 'width':
            if (this.formData.width != '') {
              this.querySelector('[name="width"]').setAttribute('aria-invalid', true);
              this.querySelector('[name="width"]').classList.add('error');
            }
            break;

          case 'height':
            if (this.formData.height != '') {
              this.querySelector('[name="height"]').setAttribute('aria-invalid', true);
              this.querySelector('[name="height"]').classList.add('error');
            }
            break;
        }
      });
    }

    /**
     * Handle next steps
     * @param {HTMLElement} input
     */
    handleSteps(input) {
      const step = input ? input.closest('[data-step]').dataset.step : this.state.step;
      const nextStep = this.options.steps[this.options.steps.indexOf(step) + 1];
      const progressStep =
        input && input.closest('[data-progress]')
          ? input.closest('[data-progress]').dataset.progress
          : this.state.progress[this.state.progress.length - 1];
      const nextProgressStep =
        this.options.progressBarSteps[this.options.progressBarSteps.indexOf(progressStep) + 1];

      switch (step) {
        case 'size':
          if (this.formData.width != '' && this.formData.height != '') {
            this.setNextStep(nextStep);
            this.setProgressBar(progressStep); // size
          }
          break;
        case 'placement':
          if (this.formData.placement != '') {
            this.setNextStep(nextStep); // chain or tape
            this.setProgressBar(progressStep); // placement
          }
          break;
        case 'chain':
          if (this.formData.chain != '') {
            this.setNextStep(nextStep); // control location
            this.setProgressBar(progressStep); // chain
          }
          break;
        case 'draw_rod':
          if (this.formData.draw_rod != '') {
            this.setNextStep(nextStep);
            this.setProgressBar(progressStep); // draw rod
          }
          break;
        case 'type_of_attachment':
          if (this.formData.type_of_attachment != '') {
            this.setNextStep(nextStep);
            this.setProgressBar(progressStep); // type of attachment
          }
          break;
        case 'option_id':
          if (this.formData.option_id != '' && this.formData.option_title != '') {
            this.setNextStep(nextStep); // quantity or control location blinds
            this.setProgressBar(progressStep); // sheeting or tape
          }
          break;
        case 'control_location':
          if (this.formData.control_location != '') {
            this.setNextStep(nextStep); // sheeting_type
            this.setProgressBar(progressStep);
          }
          break;
        case 'control_type':
          if (this.formData.control_type != '') {
            this.setNextStep(nextStep); // quantity
            this.setProgressBar(progressStep); // control type
          }
          break;
        case 'control_direction':
          if (this.options.steps.includes('control_location')) {
            if (this.formData.control_direction == 'top-down') {
              this.setNextStep(nextStep);
              this.setProgressBar(progressStep, false); // remove control step in progress
            } else if (this.formData.control_direction != '') {
              const skipStep = this.options.steps[this.options.steps.indexOf(step) + 2]; // Skip the control location field
              this.setNextStep(skipStep); // hardware colors
              this.setPrevStep(nextStep); // To hide the option id field
              this.setProgressBar(progressStep); // control direction
            }
            // Clear the control location field
            this.querySelectorAll('[name="control_location"]').forEach((input) => {
              input.checked = false;
            });
          } else if (this.formData.control_direction != '') {
            this.setNextStep(nextStep); // control location
            this.setProgressBar(progressStep); // control direction
          }
          break;
        case 'sheeting_type':
          if (!this.options.steps.includes('option_id') && this.formData.sheeting_type != '') {
            this.setNextStep(nextStep); // quantity
            this.setProgressBar(progressStep); // sheeting
          } else if (this.formData.sheeting_type == 'none') {
            const skipStep = this.options.steps[this.options.steps.indexOf(step) + 2]; // Skip option_id field
            this.setNextStep(skipStep); // quantity
            this.setPrevStep(nextStep); // To hide the option id field
            this.setProgressBar(progressStep); // sheeting
          } else if (this.formData.sheeting_type != '') {
            this.setNextStep(nextStep); // option_id
            this.setProgressBar(progressStep, false); // remove sheeting step in progress
            this.matrixColorPicker.filter(this.formData.sheeting_type);
          }
          if (this.options.steps.includes('option_id')) {
            this.matrixColorPicker.clear();
          }
          break;
        case 'tape':
          // If no options step go to next step
          if (!this.options.steps.includes('option_id') && this.formData.tape != '') {
            this.setNextStep(nextStep); // control location 2
            this.setProgressBar(progressStep); // tape
          } else if (this.formData.tape == 'no') {
            const skipStep = this.options.steps[this.options.steps.indexOf(step) + 2]; // Skip option_id field
            this.setNextStep(skipStep); // control location 2
            this.setPrevStep(nextStep); // To hide the option id field
            this.setProgressBar(progressStep); // tape
          } else if (this.formData.tape != '') {
            this.setNextStep(nextStep); // option_id
            this.setProgressBar(progressStep, false); // remove sheeting step in progress
            this.matrixColorPicker.filter('blind');
          }
          if (this.options.steps.includes('option_id')) {
            this.matrixColorPicker.clear();
          }
          break;
        case 'location':
          if (
            this.locationFreeInput &&
            handleize(this.formData.location) == this.locationFreeInput
          ) {
            this.setNextStep(nextStep); // quantity
          } else if (this.formData.location != '') {
            const skipStep = this.options.steps[this.options.steps.indexOf(step) + 1]; // Skip the location free text field
            this.setNextStep(skipStep); // quantity
            this.setPrevStep(nextStep); // To hide the location free text field
            // Clear the location free text field
            if (this.querySelector('[name="location_free_input"]')) {
              setTimeout(() => {
                this.querySelector('[name="location_free_input"]').value = '';
              }, 300);
            }
          }
          break;
        case 'location_free_input':
          if (this.formData.location_free_input != '') {
            this.setNextStep(nextStep); // color
          }
          break;
      }

      // Toggle submit button
      const submitState = this.state.progress.length == this.options.progressBarSteps.length;
      this.toggleSubmitButton(submitState);
    }

    /**
     * Set progress bar step
     * @param {string} step
     * @param {boolean} add - add step from progress array
     */
    setProgressBar(step, add = true) {
      if (!step) return;

      // Remove or add step to progress array
      if (!add) {
        this.state.progress = this.state.progress.filter((item) => item !== step);
      } else if (!this.state.progress.includes(step)) {
        this.state.progress.push(step);
      }

      // Calculate progress bar width
      const progress = (this.state.progress.length / this.options.progressBarSteps.length) * 100;

      // Set progress bar width
      this.progressBar.style.width = `${progress}%`;
    }
  }

  window.MatrixConfigurator = MatrixConfigurator;

  customElements.define('matrix-configurator', MatrixConfigurator);
}
